import { Injectable } from '@angular/core';
import { PermissionViewModel, SharedComponent, ShareUserViewModel } from '@dntlab/share-files-dialog';
import { catchError, EMPTY, firstValueFrom, map, Observable, of, switchMap } from 'rxjs';
import { SessionService } from '../auth/session.service';
import { BlockedUsersService } from '../common/services/blocked-users.service';
import {
  QueryMode,
  DefaultService as RestApiService,
  SuggestionItemTypes,
  SuggestionResponse,
  RecentAction,
} from '../swagger-api';
import { UsersSuggestionBaseService } from './users-suggestion-base.service';

@Injectable({ providedIn: 'root' })
export class UsersSuggestRecentService extends UsersSuggestionBaseService {
  public constructor(
    apiService: RestApiService,
    private readonly blockedUsers: BlockedUsersService,
    private readonly sessionService: SessionService,
  ) {
    super(apiService);
  }

  public suggestUsers(dialog: SharedComponent, keyword: string): Promise<ShareUserViewModel[]> {
    return this.suggestRecentUsersAsync(dialog, keyword);
  }

  public async suggestRecentUsersAsync(dialog: SharedComponent, keyword: string): Promise<ShareUserViewModel[]> {
    keyword = keyword.trim().toLowerCase();

    return firstValueFrom(
      this.blockedUsers.getBlockedUsersIds().pipe(
        switchMap((blockedUsersIds: string[]) => {
          return this.fetchRecentUsers(dialog, keyword, blockedUsersIds).pipe(
            switchMap((recentUsers: ShareUserViewModel[]) => {
              if (keyword.length == 0) {
                return of(recentUsers);
              }

              const recentUsersIds = recentUsers.map((userModel: ShareUserViewModel) => userModel.id);
              return this.fetchUsers(dialog, keyword, [
                ...recentUsersIds,
                ...blockedUsersIds,
                this.sessionService.userId,
              ]).pipe(
                map((suggestUsers: ShareUserViewModel[]) => {
                  return [...recentUsers, ...suggestUsers];
                }),
              );
            }),
          );
        }),
      ),
    );
  }

  public fetchRecentUsers(
    dialog: SharedComponent,
    keyword: string,
    excludedUsersIds: string[],
  ): Observable<ShareUserViewModel[]> {
    return this.apiService
      .restSearchSuggestRecentGet(
        SuggestionItemTypes.Users,
        RecentAction.FileShared,
        keyword,
        QueryMode.Names,
        excludedUsersIds,
      )
      .pipe(
        catchError(() => EMPTY),
        map((response: SuggestionResponse) =>
          this.searchResultToUserViewModelAdapter(
            (dialog['currentPermissionViewModels'] as PermissionViewModel[]) ?? [],
            response,
          ),
        ),
      );
  }
}
